import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, isToday } from "date-fns";
import { Bell, CheckCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api, get } from "@/lib/api";
import { cn } from "@/lib/utils";

export const useNotifActions = () => {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["notifications"] }); qc.invalidateQueries({ queryKey: ["notifications-unread"] }); };
  return {
    read: async (n) => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); refresh(); } },
    readAll: async () => { await api.post("/notifications/read-all"); qc.setQueryData(["notifications-unread"], 0); refresh(); },
    clearAll: async () => { await api.delete("/notifications"); qc.setQueryData(["notifications-unread"], 0); refresh(); },
  };
};

export function NotificationItem({ n, onOpen, testId }) {
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={testId}
      className={cn("flex w-full gap-3 rounded-[5px] px-3 py-2.5 text-left transition-colors duration-150 hover:bg-subtle", !n.read && "bg-brand-tint/40")}>
      <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-brand")} aria-hidden="true" />
      <span className="min-w-0">
        <span className="block text-sm text-ink">{n.title}</span>
        <span className="block text-xs text-ink-muted">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}{!n.read && <span className="sr-only"> · unread</span>}</span>
      </span>
    </button>
  );
}

export function NotificationsBell() {
  const nav = useNavigate();
  const { read, readAll, clearAll } = useNotifActions();
  const { data } = useQuery({ queryKey: ["notifications", "bell"], queryFn: () => get("/notifications", { limit: 15 }) });
  const { data: unread = data?.unread ?? 0 } = useQuery({ queryKey: ["notifications-unread"], queryFn: async () => (await get("/notifications", { limit: 1 })).unread });
  const items = data?.items || [];
  const groups = [["Today", items.filter((n) => isToday(new Date(n.created_at)))], ["Earlier", items.filter((n) => !isToday(new Date(n.created_at)))]];
  const open = async (n) => { await read(n); if (n.link) nav(n.link); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-lg p-2 text-ink-secondary transition-colors duration-150 hover:bg-subtle hover:text-ink" data-testid="notifications-bell"
          aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}>
          <Bell size={18} aria-hidden="true" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span key={unread} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }}
                className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-risk px-1 text-center text-[10px] font-semibold leading-[18px] text-white" data-testid="notifications-count">
                {unread > 99 ? "99+" : unread}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-[6px] p-0" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={readAll} disabled={!unread} data-testid="notifications-mark-all"><CheckCheck size={14} aria-hidden="true" /> Mark all read</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearAll} disabled={!items.length} data-testid="notifications-clear-all">Clear all</Button>
        </div>
        <div className="max-h-96 overflow-y-auto p-2">
          {!items.length && <p className="px-3 py-6 text-center text-sm text-ink-muted" data-testid="notifications-empty">You're all caught up.</p>}
          {groups.map(([label, list]) => list.length > 0 && (
            <div key={label} className="mb-1" data-testid={`notifications-group-${label.toLowerCase()}`}>
              <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{label}</p>
              {list.map((n) => <NotificationItem key={n.id} n={n} onOpen={open} testId={`notification-${n.id}`} />)}
            </div>
          ))}
        </div>
        <div className="border-t border-line p-2 text-center"><Link to="/app/notifications" className="text-sm text-brand hover:underline" data-testid="notifications-view-all">View all notifications</Link></div>
      </PopoverContent>
    </Popover>
  );
}
